import { useCallback, useEffect, useRef, useState } from "react";
import type { AppData } from "../../shared/types";
import { fetchAppData, getPollIntervalMs, NotReadyError } from "./fetchAppData";

export type AppDataState = {
  data: AppData | null;
  error: "not-ready" | "network" | null;
  refreshing: boolean;
  lastFetchAt: string | null;
  refetch: () => Promise<void>;
};

export function useAppData(): AppDataState {
  const [data, setData] = useState<AppData | null>(null);
  const [error, setError] = useState<"not-ready" | "network" | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState<string | null>(null);
  const isMounted = useRef(true);
  const timer = useRef<number | null>(null);
  const dataRef = useRef<AppData | null>(null);
  dataRef.current = data;

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const next = await fetchAppData();
      if (!isMounted.current) return;
      setData(next);
      setError(null);
      setLastFetchAt(new Date().toISOString());
    } catch (err) {
      if (!isMounted.current) return;
      if (err instanceof NotReadyError) {
        setError(dataRef.current ? null : "not-ready");
      } else {
        setError(dataRef.current ? "network" : "network");
      }
    } finally {
      if (isMounted.current) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    load();
    return () => {
      isMounted.current = false;
    };
  }, [load]);

  useEffect(() => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const ms = getPollIntervalMs(data);
    timer.current = window.setTimeout(() => {
      load();
    }, ms);
    return () => {
      if (timer.current) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [data, load]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  return { data, error, refreshing, lastFetchAt, refetch: load };
}
